import { useState, useEffect } from 'react'
import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt, useChainId, useBalance } from 'wagmi'
import { useQueryClient } from '@tanstack/react-query'
import { parseEther, formatEther } from 'viem'
import { getNFTContract, parseContractError } from '@/config/contract'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { MerkleTreeManager } from '@/components/MerkleTreeManager'
import { PinataUploadManager } from '@/components/PinataUploadManager'
import { Settings, Lock, Unlock, Eye, RefreshCw, DollarSign, Users, Shield, UserCog, BookOpen, Edit, Clock } from 'lucide-react'
import toast from 'react-hot-toast'

export const AdminDebug = () => {
  const { address } = useAccount()
  const chainId = useChainId()
  const contract = getNFTContract(chainId)
  const queryClient = useQueryClient()
  const { writeContract, data: hash } = useWriteContract()
  const { isSuccess } = useWaitForTransactionReceipt({ hash })

  // Read merkleRoot in main component to have access to refetch
  const { data: merkleRootRaw, refetch: refetchMerkleRoot, isLoading: isLoadingMerkleRoot } = useReadContract({ 
    ...contract, 
    functionName: 'merkleRoot',
    query: {
      enabled: !!contract.address && contract.address !== '0x',
      refetchInterval: 15000, // Auto-refresh every 15 seconds
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    }
  })

  // Convert bytes32 to string properly
  const merkleRootFormatted = merkleRootRaw 
    ? (typeof merkleRootRaw === 'string' 
        ? merkleRootRaw 
        : String(merkleRootRaw).startsWith('0x') 
          ? String(merkleRootRaw) 
          : `0x${String(merkleRootRaw)}`)
    : null

  // Debug logging
  useEffect(() => {
    console.log('🔍 Merkle Root State:', {
      raw: merkleRootRaw,
      formatted: merkleRootFormatted,
      isLoading: isLoadingMerkleRoot,
      contractAddress: contract.address,
      chainId,
      isEmpty: !merkleRootFormatted || merkleRootFormatted === '0x0000000000000000000000000000000000000000000000000000000000000000',
    })
  }, [merkleRootRaw, merkleRootFormatted, isLoadingMerkleRoot, contract.address, chainId])

  // Check if user is owner
  const { data: owner } = useReadContract({
    ...contract,
    functionName: 'owner' as any,
  })

  // Current prices from contract (for validation in setStartPrice/setEndPrice)
  const { data: contractStartPrice } = useReadContract({
    ...contract,
    functionName: 'startPrice',
  })
  const { data: contractEndPrice } = useReadContract({
    ...contract,
    functionName: 'endPrice',
  })

  // Allowed admin addresses for local development
  const ALLOWED_ADMINS = [
    '0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266', // Hardhat default deployer
    '0xcEd88f72510dfb66Cda84FA4dD80142FB81743D1', // Your wallet
    '0xD3b4ECCAB456A7E814106d4D76672ae98Ef702b6', // Configured owner
  ].map(addr => addr.toLowerCase())

  const normalizedOwner = typeof owner === 'string' ? owner.toLowerCase() : undefined
  const normalizedAddress = address?.toLowerCase()

  const isOwner = !!normalizedAddress && (
    normalizedOwner === normalizedAddress ||
    ALLOWED_ADMINS.includes(normalizedAddress)
  )

  // Form states for Write functions
  const [merkleRoot, setMerkleRoot] = useState('')
  const [baseURI, setBaseURI] = useState('')
  const [notRevealedURI, setNotRevealedURI] = useState('')
  const [startPrice, setStartPrice] = useState('')
  const [endPrice, setEndPrice] = useState('')
  const [priceDecrement, setPriceDecrement] = useState('')
  const [royaltyReceiver, setRoyaltyReceiver] = useState('')
  const [royaltyPercentage, setRoyaltyPercentage] = useState('')
  const [mintToAddress, setMintToAddress] = useState('')
  const [mintQuantity, setMintQuantity] = useState('1')
  const [newOwnerAddress, setNewOwnerAddress] = useState('')
  const [maxSupply, setMaxSupply] = useState('')
  const [refundPeriodDays, setRefundPeriodDays] = useState('')
  const [maxPerWallet, setMaxPerWallet] = useState('')
  const [maxPerTx, setMaxPerTx] = useState('')
  const [startPriceOnly, setStartPriceOnly] = useState('')
  const [endPriceOnly, setEndPriceOnly] = useState('')

  if (!address) {
    return (
      <Card className="max-w-2xl mx-auto mt-8">
        <div className="text-center py-12">
          <Shield className="w-16 h-16 mx-auto text-slate-600 mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Admin Debug Panel</h2>
          <p className="text-slate-400">Please connect your wallet to access admin functions</p>
        </div>
      </Card>
    )
  }

  if (!isOwner) {
    return (
      <Card className="max-w-2xl mx-auto mt-8">
        <div className="text-center py-12">
          <Lock className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-slate-400">Only contract owner can access this page</p>
          <p className="text-xs text-slate-500 mt-2">Your address: {address}</p>
          <p className="text-xs text-slate-500">Owner address: {String(owner || 'N/A')}</p>
        </div>
      </Card>
    )
  }

  const executeWrite = (functionName: any, args: readonly any[] = [], gasLimit?: bigint) => {
    // For localhost, use lower gas limit to avoid exceeding Hardhat's gas cap (16777216)
    // For other networks, let wagmi estimate automatically
    const isLocalhost = chainId === 31337
    const defaultGasLimit = isLocalhost ? 5000000n : undefined // 5M gas for localhost
    
    writeContract(
      {
        ...contract,
        functionName: functionName as any,
        args: args as any,
        gas: gasLimit || defaultGasLimit,
      },
      {
        onSuccess: (hash) => {
          toast.success(`${functionName} transaction submitted!`)
          console.log(`✅ Transaction submitted: ${hash}`)
          
          // If it's setMerkleRoot, wait for confirmation and refresh multiple times
          if (functionName === 'setMerkleRoot') {
            console.log('🔄 setMerkleRoot transaction submitted, will refresh after confirmation...')
            // Multiple refresh attempts with increasing delays
            const refreshAttempts = [2000, 5000, 10000, 15000, 20000]
            refreshAttempts.forEach((delay, index) => {
              setTimeout(async () => {
                try {
                  console.log(`🔄 Refresh attempt ${index + 1}/${refreshAttempts.length} after ${delay}ms`)
                  // Invalidate all queries first
                  queryClient.invalidateQueries({ queryKey: ['readContract'] })
                  // Then refetch merkleRoot specifically
                  await refetchMerkleRoot()
                  // Wait a bit and refetch again
                  await new Promise(resolve => setTimeout(resolve, 500))
                  await refetchMerkleRoot()
                  console.log(`✅ Merkle root refreshed (attempt ${index + 1})`)
                } catch (error) {
                  console.error(`❌ Error refreshing merkle root (attempt ${index + 1}):`, error)
                }
              }, delay)
            })
          }
        },
        onError: (error) => {
          const friendly = parseContractError(error as Error)
          toast.error(friendly)
        },
      }
    )
  }

  // Auto-refresh contract data after successful transaction
  useEffect(() => {
    if (isSuccess && hash) {
      console.log('✅ Transaction confirmed, refreshing merkle root...', hash)
      // Wait a bit for the transaction to be mined and indexed
      const timer = setTimeout(async () => {
        try {
          // Force refetch merkleRoot multiple times to ensure we get the latest value
          await refetchMerkleRoot()
          await new Promise(resolve => setTimeout(resolve, 1000))
          await refetchMerkleRoot()
          await new Promise(resolve => setTimeout(resolve, 1000))
          await refetchMerkleRoot()
          
          // Invalidate all contract queries to force refresh
          queryClient.invalidateQueries({ queryKey: ['readContract'] })
          
          console.log('✅ Merkle root refreshed after transaction confirmation')
          toast.success('Contract data refreshed! Merkle root updated.')
        } catch (error) {
          console.error('❌ Error refreshing merkle root:', error)
          toast.error('Failed to refresh merkle root. Please refresh manually.')
        }
      }, 2000) // Wait 2 seconds after confirmation
      return () => clearTimeout(timer)
    }
  }, [isSuccess, hash, queryClient, refetchMerkleRoot])

  return (
    <div className="max-w-7xl mx-auto mt-8 space-y-6">
      {/* Header */}
      <Card>
        <div className="flex items-center gap-3">
          <Settings className="w-8 h-8 text-purple-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Admin Debug Panel</h1>
            <p className="text-sm text-slate-400">Contract Owner Functions - Remix Style Interface</p>
          </div>
        </div>
      </Card>

      {/* Tools */}
      <MerkleTreeManager />
      <PinataUploadManager />

      {/* READ FUNCTIONS SECTION */}
      <Card className="border-l-4 border-l-blue-500">
        <div className="flex items-center gap-3 mb-6">
          <BookOpen className="w-6 h-6 text-blue-400" />
          <h2 className="text-2xl font-bold text-white">📖 READ FUNCTIONS</h2>
          <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs font-medium rounded-full">View Only</span>
        </div>
        <ReadFunctionsSection merkleRoot={merkleRootFormatted} />
      </Card>

      {/* WRITE FUNCTIONS SECTION */}
      <Card className="border-l-4 border-l-orange-500">
        <div className="flex items-center gap-3 mb-6">
          <Edit className="w-6 h-6 text-orange-400" />
          <h2 className="text-2xl font-bold text-white">✏️ WRITE FUNCTIONS</h2>
          <span className="px-3 py-1 bg-orange-500/20 text-orange-400 text-xs font-medium rounded-full">Owner Only</span>
        </div>
        <WriteFunctionsSection
          executeWrite={executeWrite}
          merkleRoot={merkleRoot}
          setMerkleRoot={setMerkleRoot}
          refetchMerkleRoot={refetchMerkleRoot}
          queryClient={queryClient}
          hash={hash}
          baseURI={baseURI}
          setBaseURI={setBaseURI}
          notRevealedURI={notRevealedURI}
          setNotRevealedURI={setNotRevealedURI}
          startPrice={startPrice}
          setStartPrice={setStartPrice}
          endPrice={endPrice}
          setEndPrice={setEndPrice}
          priceDecrement={priceDecrement}
          setPriceDecrement={setPriceDecrement}
          royaltyReceiver={royaltyReceiver}
          setRoyaltyReceiver={setRoyaltyReceiver}
          royaltyPercentage={royaltyPercentage}
          setRoyaltyPercentage={setRoyaltyPercentage}
          mintToAddress={mintToAddress}
          setMintToAddress={setMintToAddress}
          mintQuantity={mintQuantity}
          setMintQuantity={setMintQuantity}
          newOwnerAddress={newOwnerAddress}
          setNewOwnerAddress={setNewOwnerAddress}
          maxSupply={maxSupply}
          setMaxSupply={setMaxSupply}
          refundPeriodDays={refundPeriodDays}
          setRefundPeriodDays={setRefundPeriodDays}
          maxPerWallet={maxPerWallet}
          setMaxPerWallet={setMaxPerWallet}
          maxPerTx={maxPerTx}
          setMaxPerTx={setMaxPerTx}
          startPriceOnly={startPriceOnly}
          setStartPriceOnly={setStartPriceOnly}
          endPriceOnly={endPriceOnly}
          setEndPriceOnly={setEndPriceOnly}
          currentStartPrice={contractStartPrice}
          currentEndPrice={contractEndPrice}
        />
      </Card>
    </div>
  )
}

// ============ READ FUNCTIONS SECTION ============
const ReadFunctionsSection = ({ merkleRoot: merkleRootProp }: { merkleRoot?: any }) => {
  const chainId = useChainId()
  const contract = getNFTContract(chainId)
  const { address } = useAccount()
  const queryClient = useQueryClient()

  // Use prop if provided, otherwise read from contract
  const { data: merkleRootRaw, refetch: refetchMerkleRoot, isLoading: isLoadingMerkleRoot, error: merkleRootError } = useReadContract({ 
    ...contract, 
    functionName: 'merkleRoot',
    query: {
      refetchInterval: 10000, // Auto-refresh every 10 seconds
      enabled: !!contract.address && contract.address !== '0x', // Always read from contract, prop is just for display
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    }
  })

  // Always use fetched value from contract, prop is just for reference
  const merkleRootValue = merkleRootRaw

  // Convert bytes32 to string properly
  const merkleRoot = merkleRootValue 
    ? (typeof merkleRootValue === 'string' 
        ? merkleRootValue 
        : String(merkleRootValue).startsWith('0x') 
          ? String(merkleRootValue) 
          : `0x${String(merkleRootValue)}`)
    : null

  // Check if merkleRoot is zero/empty
  const isEmptyMerkleRoot = !merkleRoot || 
    merkleRoot === '0x0000000000000000000000000000000000000000000000000000000000000000' || 
    merkleRoot === '0x' ||
    merkleRoot === '0x0'

  // Debug logging for merkleRoot
  useEffect(() => {
    console.log('🔍 merkleRoot debug:', {
      merkleRootProp,
      merkleRootRaw,
      merkleRootValue,
      merkleRoot,
      isEmptyMerkleRoot,
      isLoadingMerkleRoot,
      error: merkleRootError?.message,
      contractAddress: contract.address,
      chainId,
      type: typeof merkleRootValue,
    })
  }, [merkleRootProp, merkleRootRaw, merkleRootValue, merkleRoot, isEmptyMerkleRoot, isLoadingMerkleRoot, merkleRootError, contract.address, chainId])

  // All read functions
  const { data: owner } = useReadContract({ ...contract, functionName: 'owner' as any })
  const { data: paused } = useReadContract({ ...contract, functionName: 'paused' })
  const { data: revealed } = useReadContract({ ...contract, functionName: 'revealed' })
  const { data: whitelistActive } = useReadContract({ ...contract, functionName: 'whitelistActive' })
  const { data: currentPrice } = useReadContract({ ...contract, functionName: 'getCurrentPrice' })
  const { data: totalMinted } = useReadContract({ ...contract, functionName: 'totalMinted' })
  const { data: maxSupply } = useReadContract({ ...contract, functionName: 'MAX_SUPPLY' })
  const { data: maxPerWallet } = useReadContract({ ...contract, functionName: 'MAX_PER_WALLET' })
  const { data: maxPerTx } = useReadContract({ ...contract, functionName: 'MAX_PER_TX' })
  const { data: startPrice } = useReadContract({ ...contract, functionName: 'startPrice' })
  const { data: endPrice } = useReadContract({ ...contract, functionName: 'endPrice' })
  const { data: priceDecrement } = useReadContract({ ...contract, functionName: 'priceDecrement' })
  const { data: auctionStartTime } = useReadContract({ ...contract, functionName: 'auctionStartTime' })
  const { data: royaltyReceiver } = useReadContract({ ...contract, functionName: 'royaltyReceiver' })
  const { data: royaltyPercentage } = useReadContract({ ...contract, functionName: 'royaltyPercentage' })
  const { data: notRevealedURI } = useReadContract({ ...contract, functionName: 'notRevealedURI' })
  const { data: baseTokenURI } = useReadContract({ ...contract, functionName: 'getBaseTokenURI' as any })
  const { data: refundPeriod } = useReadContract({ ...contract, functionName: 'REFUND_PERIOD' as any })
  const { data: numberMinted } = useReadContract({ 
    ...contract, 
    functionName: 'numberMinted',
    args: address ? [address] : undefined,
    query: { enabled: !!address }
  })
  const { data: balance } = useReadContract({ 
    ...contract, 
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: { enabled: !!address }
  })
  // Use useBalance to get contract balance (works without getContractBalance function in contract)
  const { data: contractBalanceData } = useBalance({ 
    address: contract.address,
    query: { refetchInterval: 10000 }
  })
  const contractBalance = contractBalanceData?.value

  const readFunctions = [
    { label: 'owner()', value: String(owner || 'N/A'), type: 'address' },
    { label: 'paused()', value: paused ? 'true' : 'false', type: 'bool' },
    { label: 'revealed()', value: revealed ? 'true' : 'false', type: 'bool' },
    { label: 'whitelistActive()', value: whitelistActive ? 'true' : 'false', type: 'bool' },
    { label: 'getCurrentPrice()', value: currentPrice ? `${formatEther(currentPrice)} ETH/BNB` : '-', type: 'uint256' },
    { label: 'totalMinted()', value: totalMinted?.toString() || '0', type: 'uint256' },
    { label: 'MAX_SUPPLY', value: maxSupply?.toString() || '0', type: 'uint256' },
    { label: 'MAX_PER_WALLET', value: maxPerWallet?.toString() || '0', type: 'uint256' },
    { label: 'MAX_PER_TX', value: maxPerTx?.toString() || '0', type: 'uint256' },
    { label: 'startPrice', value: startPrice ? `${formatEther(startPrice)} ETH/BNB` : '-', type: 'uint256' },
    { label: 'endPrice', value: endPrice ? `${formatEther(endPrice)} ETH/BNB` : '-', type: 'uint256' },
    { label: 'priceDecrement', value: priceDecrement ? `${formatEther(priceDecrement)} ETH/BNB` : '-', type: 'uint256' },
    { label: 'auctionStartTime', value: auctionStartTime ? new Date(Number(auctionStartTime) * 1000).toLocaleString() : '-', type: 'uint256' },
    { label: 'merkleRoot', value: !isEmptyMerkleRoot && merkleRoot ? String(merkleRoot) : (isLoadingMerkleRoot ? 'Loading...' : 'N/A (not set)'), type: 'bytes32' },
    { label: 'royaltyReceiver', value: String(royaltyReceiver || 'N/A'), type: 'address' },
    { label: 'royaltyPercentage', value: royaltyPercentage ? `${Number(royaltyPercentage) / 100}%` : '-', type: 'uint96' },
    { label: 'notRevealedURI', value: String(notRevealedURI || 'N/A'), type: 'string' },
    {
      label: 'getBaseTokenURI() — Set Base URI',
      value: baseTokenURI !== undefined
        ? (baseTokenURI && String(baseTokenURI).length > 0
          ? `✅ Установлен: ${String(baseTokenURI)}`
          : '❌ Не установлен (пусто)')
        : '-',
      type: 'string',
    },
    { label: 'REFUND_PERIOD', value: refundPeriod ? `${Number(refundPeriod) / 86400} days` : '-', type: 'uint256' },
    { label: `numberMinted(${address?.slice(0, 10)}...)`, value: numberMinted?.toString() || '0', type: 'uint256' },
    { label: `balanceOf(${address?.slice(0, 10)}...)`, value: balance?.toString() || '0', type: 'uint256' },
    { label: 'getContractBalance()', value: contractBalance ? `${formatEther(contractBalance)} ETH/BNB` : '0', type: 'uint256' },
  ]

  return (
    <div className="space-y-4">
      <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg mb-4 flex items-center justify-between">
        <p className="text-blue-400 text-sm">
          💡 <strong>Read Functions</strong> - These functions don't modify the contract state. They're free to call and return current values.
        </p>
        <Button 
          onClick={async () => {
            console.log('🔄 Manual refresh triggered')
            // Force refetch merkleRoot specifically multiple times
            try {
              await refetchMerkleRoot()
              await new Promise(resolve => setTimeout(resolve, 500))
              await refetchMerkleRoot()
              await new Promise(resolve => setTimeout(resolve, 500))
              await refetchMerkleRoot()
              
              // Also invalidate all queries
              queryClient.invalidateQueries({ queryKey: ['readContract'] })
              
              console.log('✅ Manual refresh completed')
              toast.success('Contract data refreshed!')
            } catch (error) {
              console.error('❌ Refetch error:', error)
              toast.error('Failed to refresh data')
            }
          }} 
          variant="secondary" 
          size="sm"
          className="flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {readFunctions.map((func, index) => (
          <div key={index} className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
            <div className="flex items-start justify-between mb-2">
              <p className="text-blue-400 font-mono text-xs font-semibold">{func.label}</p>
              <span className="px-2 py-0.5 bg-slate-700 text-slate-400 text-xs rounded">{func.type}</span>
            </div>
            <p className="text-white font-mono text-sm break-all">{func.value}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

// ============ WRITE FUNCTIONS SECTION ============
const WriteFunctionsSection = ({
  executeWrite,
  merkleRoot, setMerkleRoot,
  refetchMerkleRoot,
  queryClient,
  hash,
  baseURI, setBaseURI,
  notRevealedURI, setNotRevealedURI,
  startPrice, setStartPrice,
  endPrice, setEndPrice,
  priceDecrement, setPriceDecrement,
  royaltyReceiver, setRoyaltyReceiver,
  royaltyPercentage, setRoyaltyPercentage,
  mintToAddress, setMintToAddress,
  mintQuantity, setMintQuantity,
  newOwnerAddress, setNewOwnerAddress,
  maxSupply, setMaxSupply,
  refundPeriodDays, setRefundPeriodDays,
  maxPerWallet, setMaxPerWallet,
  maxPerTx, setMaxPerTx,
  startPriceOnly, setStartPriceOnly,
  endPriceOnly, setEndPriceOnly,
  currentStartPrice,
  currentEndPrice,
}: any) => {
  const chainId = useChainId()
  const contract = getNFTContract(chainId)
  
  // Read contract balance using useBalance (works without getContractBalance function in contract)
  const { data: contractBalanceData, refetch: refetchBalance } = useBalance({ 
    address: contract.address,
    query: {
      refetchInterval: 10000,
    }
  })
  const contractBalance = contractBalanceData?.value

  const getExplorerTxBase = (chainId?: number) => {
    switch (chainId) {
      case 1:
        return 'https://etherscan.io/tx/'
      case 11155111:
        return 'https://sepolia.etherscan.io/tx/'
      case 56:
        return 'https://bscscan.com/tx/'
      case 97:
        return 'https://testnet.bscscan.com/tx/'
      default:
        return ''
    }
  }

  const explorerTxBase = getExplorerTxBase(chainId)

  return (
    <div className="space-y-6">
      <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg mb-4">
        <p className="text-orange-400 text-sm">
          ⚠️ <strong>Write Functions</strong> - These functions modify the contract state and require gas. Only the contract owner can execute these.
        </p>
        <p className="text-slate-400 text-xs mt-2">
          💡 <strong>Note:</strong> All values can now be changed! MAX_SUPPLY, REFUND_PERIOD, MAX_PER_WALLET, MAX_PER_TX, Start Price, and End Price are all variables.
        </p>
      </div>

      {/* Pause Control */}
      <WriteFunctionCard
        title="pause() / unpause()"
        description="Pause or unpause all contract functions"
        icon={<Lock className="w-5 h-5" />}
      >
        <div className="flex gap-4">
          <Button onClick={() => executeWrite('pause')} variant="destructive" className="flex items-center gap-2">
            <Lock className="w-4 h-4" />
            pause()
          </Button>
          <Button onClick={() => executeWrite('unpause')} className="flex items-center gap-2">
            <Unlock className="w-4 h-4" />
            unpause()
          </Button>
        </div>
      </WriteFunctionCard>

      {/* Set Start Price Only */}
      <WriteFunctionCard
        title="setStartPrice(uint256 newStartPrice)"
        description="Update ONLY the start price (in ETH/BNB). ⚠️ Must be GREATER than end price!"
        icon={<DollarSign className="w-5 h-5" />}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Start Price (ETH/BNB)</label>
            <input
              type="text"
              value={startPriceOnly}
              onChange={(e) => setStartPriceOnly(e.target.value)}
              placeholder="0.5"
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono"
            />
            <p className="text-xs text-slate-400 mt-1">
              Current end price: {currentEndPrice ? formatEther(currentEndPrice) : 'loading...'} ETH/BNB
              {currentEndPrice && startPriceOnly && parseFloat(startPriceOnly) <= parseFloat(formatEther(currentEndPrice)) && (
                <span className="text-red-400 block mt-1">
                  ⚠️ Start price must be greater than end price!
                </span>
              )}
            </p>
          </div>
          <Button
            onClick={() => {
              if (!startPriceOnly || startPriceOnly.trim() === '') {
                toast.error('Please enter a start price')
                return
              }
              
              const startPriceValue = parseFloat(startPriceOnly)
              
              if (isNaN(startPriceValue) || startPriceValue <= 0) {
                toast.error('Start price must be a number greater than 0')
                return
              }
              
              const endPriceValue = currentEndPrice ? parseFloat(formatEther(currentEndPrice)) : null
              
              if (currentEndPrice != null && startPriceValue <= (endPriceValue ?? 0)) {
                toast.error(`Start price (${startPriceOnly} ETH/BNB) must be GREATER than current end price (${formatEther(currentEndPrice)} ETH/BNB)`)
                return
              }
              
              if (!currentEndPrice) {
                toast('Current end price not loaded. Ensure new start price > end price or tx may revert.', { icon: '⚠️' })
              }
              
              executeWrite('setStartPrice', [parseEther(startPriceOnly)])
            }}
            className="flex items-center gap-2"
          >
            <DollarSign className="w-4 h-4" />
            setStartPrice()
          </Button>
        </div>
      </WriteFunctionCard>

      {/* Set End Price Only */}
      <WriteFunctionCard
        title="setEndPrice(uint256 newEndPrice)"
        description="Update ONLY the end price (in ETH/BNB). ⚠️ Must be LESS than start price!"
        icon={<DollarSign className="w-5 h-5" />}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">End Price (ETH/BNB)</label>
            <input
              type="text"
              value={endPriceOnly}
              onChange={(e) => setEndPriceOnly(e.target.value)}
              placeholder="0.1"
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono"
            />
            <p className="text-xs text-slate-400 mt-1">
              Current start price: {currentStartPrice ? formatEther(currentStartPrice) : 'loading...'} ETH/BNB
              {currentStartPrice && endPriceOnly && parseFloat(endPriceOnly) >= parseFloat(formatEther(currentStartPrice)) && (
                <span className="text-red-400 block mt-1">
                  ⚠️ End price must be less than start price!
                </span>
              )}
            </p>
          </div>
          <Button
            onClick={() => {
              if (!endPriceOnly || endPriceOnly.trim() === '') {
                toast.error('Please enter an end price')
                return
              }
              
              const endPriceValue = parseFloat(endPriceOnly)
              
              if (isNaN(endPriceValue) || endPriceValue <= 0) {
                toast.error('End price must be a number greater than 0')
                return
              }
              
              const startPriceValue = currentStartPrice ? parseFloat(formatEther(currentStartPrice)) : null
              
              if (currentStartPrice != null && endPriceValue >= (startPriceValue ?? Infinity)) {
                toast.error(`End price (${endPriceOnly} ETH/BNB) must be LESS than current start price (${formatEther(currentStartPrice)} ETH/BNB)`)
                return
              }
              
              if (!currentStartPrice) {
                toast('Current start price not loaded. Ensure new end price < start price or tx may revert.', { icon: '⚠️' })
              }
              
              executeWrite('setEndPrice', [parseEther(endPriceOnly)])
            }}
            className="flex items-center gap-2"
          >
            <DollarSign className="w-4 h-4" />
            setEndPrice()
          </Button>
        </div>
      </WriteFunctionCard>

      {/* Auction Configuration */}
      <WriteFunctionCard
        title="updateAuctionConfig(uint256 newStartPrice, uint256 newEndPrice, uint256 newPriceDecrement)"
        description="Update all Dutch auction pricing at once. ⚠️ This changes the Start Price shown on the frontend!"
        icon={<DollarSign className="w-5 h-5" />}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Start Price (ETH/BNB)</label>
              <input
                type="text"
                value={startPrice}
                onChange={(e) => setStartPrice(e.target.value)}
                placeholder="0.5"
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">End Price (ETH/BNB)</label>
              <input
                type="text"
                value={endPrice}
                onChange={(e) => setEndPrice(e.target.value)}
                placeholder="0.1"
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Price Decrement (ETH/BNB)</label>
              <input
                type="text"
                value={priceDecrement}
                onChange={(e) => setPriceDecrement(e.target.value)}
                placeholder="0.05"
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono"
              />
            </div>
          </div>
          <Button
            onClick={() => {
              const start = parseFloat(startPrice || '0')
              const end = parseFloat(endPrice || '0')
              const decrement = parseFloat(priceDecrement || '0')
              
              if (start <= 0 || end <= 0 || decrement <= 0) {
                toast.error('All values must be greater than 0')
                return
              }
              
              if (start <= end) {
                toast.error('Start Price must be greater than End Price')
                return
              }
              
              const maxDiscount = start - end
              if (decrement > maxDiscount) {
                toast.error(`Price Decrement (${decrement}) cannot exceed the price range (${maxDiscount.toFixed(6)} ETH)`)
                return
              }
              
              executeWrite('updateAuctionConfig', [
                parseEther(startPrice || '0'),
                parseEther(endPrice || '0'),
                parseEther(priceDecrement || '0'),
              ])
            }}
            className="flex items-center gap-2"
          >
            <DollarSign className="w-4 h-4" />
            updateAuctionConfig()
          </Button>
        </div>
      </WriteFunctionCard>

      {/* Reset Auction */}
      <WriteFunctionCard
        title="resetAuction()"
        description="Reset the auction timer to current block timestamp"
        icon={<RefreshCw className="w-5 h-5" />}
      >
        <Button onClick={() => executeWrite('resetAuction')} variant="secondary" className="flex items-center gap-2">
          <RefreshCw className="w-4 h-4" />
          resetAuction()
        </Button>
      </WriteFunctionCard>

      {/* Merkle Root */}
      <WriteFunctionCard
        title="setMerkleRoot(bytes32 newMerkleRoot)"
        description="Set the Merkle root for whitelist verification"
        icon={<Users className="w-5 h-5" />}
      >
        <div className="space-y-4">
          <input
            type="text"
            value={merkleRoot}
            onChange={(e) => setMerkleRoot(e.target.value)}
            placeholder="0x..."
            className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono"
          />
          <div className="flex gap-2">
            <Button onClick={() => executeWrite('setMerkleRoot', [merkleRoot])} className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              setMerkleRoot()
            </Button>
            <Button 
              onClick={async () => {
                console.log('🔄 Force refresh merkle root...')
                try {
                  await refetchMerkleRoot()
                  queryClient.invalidateQueries({ queryKey: ['readContract'] })
                  toast.success('Merkle root refreshed!')
                } catch (error) {
                  console.error('Error:', error)
                  toast.error('Failed to refresh')
                }
              }} 
              variant="secondary"
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
          {hash && (
            <div className="text-xs text-slate-400">
              Last transaction:{' '}
              {explorerTxBase ? (
                <a
                  href={`${explorerTxBase}${hash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:underline"
                >
                  {hash.slice(0, 10)}...{hash.slice(-8)}
                </a>
              ) : (
                <span>{hash.slice(0, 10)}...{hash.slice(-8)}</span>
              )}
            </div>
          )}
        </div>
      </WriteFunctionCard>

      {/* Whitelist Active */}
      <WriteFunctionCard
        title="setWhitelistActive(bool _active)"
        description="Enable or disable whitelist requirement"
        icon={<Users className="w-5 h-5" />}
      >
        <div className="flex gap-4">
          <Button onClick={() => executeWrite('setWhitelistActive', [true])} variant="secondary">
            setWhitelistActive(true)
          </Button>
          <Button onClick={() => executeWrite('setWhitelistActive', [false])} variant="secondary">
            setWhitelistActive(false)
          </Button>
        </div>
      </WriteFunctionCard>

      {/* Reveal */}
      <WriteFunctionCard
        title="reveal(string _baseURI)"
        description="Reveal the collection with final metadata URI"
        icon={<Eye className="w-5 h-5" />}
      >
        <div className="space-y-4">
          <input
            type="text"
            value={baseURI}
            onChange={(e) => setBaseURI(e.target.value)}
            placeholder="ipfs://QmXxx/"
            className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
          />
          <Button onClick={() => executeWrite('reveal', [baseURI])} className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            reveal()
          </Button>
        </div>
      </WriteFunctionCard>

      {/* Set Base URI */}
      <WriteFunctionCard
        title="setBaseURI(string newBaseURI)"
        description="Update base URI after reveal"
        icon={<Settings className="w-5 h-5" />}
      >
        <div className="space-y-4">
          <input
            type="text"
            value={baseURI}
            onChange={(e) => setBaseURI(e.target.value)}
            placeholder="ipfs://QmXxx/"
            className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
          />
          <Button onClick={() => executeWrite('setBaseURI', [baseURI])} variant="secondary" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            setBaseURI()
          </Button>
        </div>
      </WriteFunctionCard>

      {/* Set Not Revealed URI */}
      <WriteFunctionCard
        title="setNotRevealedURI(string _notRevealedURI)"
        description="Set URI for unrevealed tokens"
        icon={<Eye className="w-5 h-5" />}
      >
        <div className="space-y-4">
          <input
            type="text"
            value={notRevealedURI}
            onChange={(e) => setNotRevealedURI(e.target.value)}
            placeholder="ipfs://QmYyy/hidden.json"
            className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
          />
          <Button onClick={() => executeWrite('setNotRevealedURI', [notRevealedURI])} variant="secondary" className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            setNotRevealedURI()
          </Button>
        </div>
      </WriteFunctionCard>

      {/* Royalty */}
      <WriteFunctionCard
        title="setRoyaltyInfo(address newRoyaltyReceiver, uint96 newRoyaltyPercentage)"
        description="Set royalty receiver and percentage (in basis points, e.g. 750 = 7.5%)"
        icon={<DollarSign className="w-5 h-5" />}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Royalty Receiver</label>
            <input
              type="text"
              value={royaltyReceiver}
              onChange={(e) => setRoyaltyReceiver(e.target.value)}
              placeholder="0x..."
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Royalty % (basis points)</label>
            <input
              type="text"
              value={royaltyPercentage}
              onChange={(e) => setRoyaltyPercentage(e.target.value)}
              placeholder="750"
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono"
            />
          </div>
          <Button
            onClick={() =>
              executeWrite('setRoyaltyInfo', [royaltyReceiver, parseInt(royaltyPercentage || '0')])
            }
            className="flex items-center gap-2"
          >
            <DollarSign className="w-4 h-4" />
            setRoyaltyInfo()
          </Button>
        </div>
      </WriteFunctionCard>

      {/* Owner Mint */}
      <WriteFunctionCard
        title="ownerMint(address to, uint256 quantity)"
        description="Mint NFTs directly to any address (owner only)"
        icon={<Users className="w-5 h-5" />}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Recipient Address</label>
            <input
              type="text"
              value={mintToAddress}
              onChange={(e) => setMintToAddress(e.target.value)}
              placeholder="0x..."
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Quantity</label>
            <input
              type="number"
              value={mintQuantity}
              onChange={(e) => setMintQuantity(e.target.value)}
              min="1"
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono"
            />
          </div>
          <Button
            onClick={() =>
              executeWrite('ownerMint', [mintToAddress, parseInt(mintQuantity || '1')])
            }
            className="flex items-center gap-2"
          >
            <Users className="w-4 h-4" />
            ownerMint()
          </Button>
        </div>
      </WriteFunctionCard>

      {/* Transfer Ownership */}
      <WriteFunctionCard
        title="transferOwnership(address newOwner)"
        description="⚠️ Transfer contract ownership (IRREVERSIBLE!)"
        icon={<UserCog className="w-5 h-5" />}
        warning
      >
        <div className="space-y-4">
          <div className="p-4 bg-red-900/20 border border-red-700 rounded-lg">
            <p className="text-red-400 text-sm font-medium">⚠️ Warning: This action is irreversible!</p>
            <p className="text-slate-400 text-xs mt-1">Transferring ownership will give full control of the contract to the new address.</p>
          </div>
          <input
            type="text"
            value={newOwnerAddress}
            onChange={(e) => setNewOwnerAddress(e.target.value)}
            placeholder="0x..."
            className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono"
          />
          <Button
            onClick={() => {
              if (!newOwnerAddress || !newOwnerAddress.startsWith('0x') || newOwnerAddress.length !== 42) {
                toast.error('Please enter a valid Ethereum address')
                return
              }
              if (window.confirm(`Are you sure you want to transfer ownership to ${newOwnerAddress}? This action cannot be undone!`)) {
                executeWrite('transferOwnership', [newOwnerAddress])
              }
            }}
            variant="destructive"
            className="flex items-center gap-2"
          >
            <UserCog className="w-4 h-4" />
            transferOwnership()
          </Button>
        </div>
      </WriteFunctionCard>

      {/* Set Max Supply */}
      <WriteFunctionCard
        title="setMaxSupply(uint256 newMaxSupply)"
        description="⚠️ Update Total Supply (MAX_SUPPLY). Cannot be less than already minted!"
        icon={<Users className="w-5 h-5" />}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">New Max Supply</label>
            <input
              type="number"
              value={maxSupply}
              onChange={(e) => setMaxSupply(e.target.value)}
              placeholder="10000"
              min="1"
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono"
            />
          </div>
          <Button
            onClick={() => executeWrite('setMaxSupply', [parseInt(maxSupply || '0')])}
            className="flex items-center gap-2"
          >
            <Users className="w-4 h-4" />
            setMaxSupply()
          </Button>
        </div>
      </WriteFunctionCard>

      {/* Set Max Per Wallet */}
      <WriteFunctionCard
        title="setMaxPerWallet(uint256 newMaxPerWallet)"
        description="Update maximum NFTs per wallet"
        icon={<Users className="w-5 h-5" />}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Max Per Wallet</label>
            <input
              type="number"
              value={maxPerWallet}
              onChange={(e) => setMaxPerWallet(e.target.value)}
              placeholder="10"
              min="1"
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono"
            />
          </div>
          <Button
            onClick={() => executeWrite('setMaxPerWallet', [parseInt(maxPerWallet || '0')])}
            className="flex items-center gap-2"
          >
            <Users className="w-4 h-4" />
            setMaxPerWallet()
          </Button>
        </div>
      </WriteFunctionCard>

      {/* Set Max Per TX */}
      <WriteFunctionCard
        title="setMaxPerTx(uint256 newMaxPerTx)"
        description="Update maximum NFTs per transaction"
        icon={<Users className="w-5 h-5" />}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Max Per TX</label>
            <input
              type="number"
              value={maxPerTx}
              onChange={(e) => setMaxPerTx(e.target.value)}
              placeholder="5"
              min="1"
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono"
            />
          </div>
          <Button
            onClick={() => executeWrite('setMaxPerTx', [parseInt(maxPerTx || '0')])}
            className="flex items-center gap-2"
          >
            <Users className="w-4 h-4" />
            setMaxPerTx()
          </Button>
        </div>
      </WriteFunctionCard>

      {/* Set Refund Period */}
      <WriteFunctionCard
        title="setRefundPeriod(uint256 newRefundPeriod)"
        description="⚠️ Update Refund Period in seconds (e.g., 7 days = 604800). Minimum 1 day!"
        icon={<Clock className="w-5 h-5" />}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Refund Period (Days)</label>
            <input
              type="number"
              value={refundPeriodDays}
              onChange={(e) => setRefundPeriodDays(e.target.value)}
              placeholder="7"
              min="1"
              className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white font-mono"
            />
            <p className="text-xs text-slate-400 mt-1">
              Will be converted to seconds: {refundPeriodDays ? `${parseInt(refundPeriodDays || '0') * 86400}` : '0'} seconds
            </p>
          </div>
          <Button
            onClick={() => {
              const seconds = parseInt(refundPeriodDays || '0') * 86400
              if (seconds < 86400) {
                toast.error('Refund period must be at least 1 day (86400 seconds)')
                return
              }
              executeWrite('setRefundPeriod', [seconds])
            }}
            className="flex items-center gap-2"
          >
            <Clock className="w-4 h-4" />
            setRefundPeriod()
          </Button>
        </div>
      </WriteFunctionCard>

      {/* Revenue Management */}
      <WriteFunctionCard
        title="distributeRevenue() / emergencyWithdraw()"
        description="Distribute revenue to shareholders or emergency withdraw"
        icon={<DollarSign className="w-5 h-5" />}
      >
        <div className="space-y-4">
          {/* Contract Balance Display */}
          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-400 text-sm font-medium">💰 Contract Balance (Revenue)</p>
                <p className="text-2xl font-bold text-white mt-1">
                  {contractBalance ? `${formatEther(contractBalance)} ETH/BNB` : '0'}
                </p>
              </div>
              <Button 
                onClick={() => refetchBalance()} 
                variant="secondary" 
                size="sm"
                className="flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
            </div>
          </div>
          
          <div className="flex gap-4">
            <Button onClick={() => executeWrite('distributeRevenue')} className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              distributeRevenue()
            </Button>
            <Button onClick={() => executeWrite('emergencyWithdraw')} variant="destructive" className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              emergencyWithdraw()
            </Button>
          </div>
        </div>
      </WriteFunctionCard>
    </div>
  )
}

// Helper component for write function cards
const WriteFunctionCard = ({ title, description, icon, children, warning }: any) => (
  <div className={`p-6 bg-slate-800/50 border ${warning ? 'border-red-500/50' : 'border-slate-700'} rounded-lg`}>
    <div className="flex items-start gap-3 mb-4">
      <div className="text-orange-400">{icon}</div>
      <div className="flex-1">
        <h3 className="text-lg font-bold text-white font-mono mb-1">{title}</h3>
        <p className="text-slate-400 text-sm">{description}</p>
      </div>
    </div>
    <div className="mt-4">{children}</div>
  </div>
)
